import React, { useState } from 'react';
import { 
  Book, Users, FileText, MessageSquare, DollarSign, Calendar, 
  Settings, Shield, Upload, Download, Mail, Bell, PenTool,
  Search, ChevronDown, ChevronRight, ExternalLink, HelpCircle,
  UserPlus, Archive, Trash2, Ban, Power, KeyRound, Send,
  FolderOpen, Tag, Eye, Clock, CheckCircle, Building, BarChart3,
  Sparkles, Zap, Star
} from 'lucide-react';
import { Input } from '../ui/input';

const Documentation = () => {
  const [expandedSections, setExpandedSections] = useState(['getting-started']);
  const [searchQuery, setSearchQuery] = useState('');
  const [showWhatsNew, setShowWhatsNew] = useState(true);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // What's New items - update this when new features are added
  const whatsNewItems = [
    {
      date: 'January 2026',
      title: 'Resend Invite Button',
      description: 'Re-send portal invitations to clients who haven\'t activated their account yet.',
      icon: <Send className="w-4 h-4" />,
      isNew: true
    },
    {
      date: 'January 2026',
      title: 'Interactive Onboarding Tour',
      description: 'New guided walkthrough helps users learn the portal features step by step.',
      icon: <Sparkles className="w-4 h-4" />,
      isNew: true
    },
    {
      date: 'January 2026',
      title: 'Activity Logs',
      description: 'Track all admin and client actions with detailed activity logging in Settings.',
      icon: <Clock className="w-4 h-4" />,
      isNew: true
    },
    {
      date: 'January 2026',
      title: 'Bulk Client Actions',
      description: 'Select multiple clients to archive, suspend, or delete them in one action.',
      icon: <Users className="w-4 h-4" />,
      isNew: false
    },
    {
      date: 'January 2026',
      title: 'Client Access Control',
      description: 'Suspend, revoke, or reactivate client portal access with granular controls.',
      icon: <Shield className="w-4 h-4" />,
      isNew: false
    },
    {
      date: 'January 2026',
      title: 'Admin Password Reset',
      description: 'Send password reset links directly to clients from their profile page.',
      icon: <KeyRound className="w-4 h-4" />,
      isNew: false
    }
  ];

  const documentationSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <Book className="w-5 h-5" />,
      content: [
        {
          title: 'Welcome to PPMT Client Portal',
          description: 'The PPMT Strategic Group Client Portal is a comprehensive platform designed to streamline client management, document sharing, invoicing, and communication for accounting professionals.'
        },
        {
          title: 'First Steps',
          description: 'After logging in, you\'ll see the main dashboard with key metrics and recent activity. Use the sidebar navigation to access different sections of the portal.',
          steps: [
            'Review your dashboard for an overview of clients, files, and messages',
            'Add your first client using the "Add Client" button',
            'Upload documents and share them with clients',
            'Set up email templates for automated notifications'
          ]
        },
        {
          title: 'Navigation Overview',
          description: 'The sidebar provides quick access to all portal features:',
          steps: [
            'Dashboard - Overview and recent activity',
            'Leads - Manage potential clients',
            'Clients - Manage active clients',
            'Files - Document management',
            'Messages - Client communication',
            'Services - Service packages',
            'Invoicing - Create and track invoices',
            'Tax Deadlines - Important date tracking',
            'Team - Manage staff members',
            'Settings - Portal configuration'
          ]
        }
      ]
    },
    {
      id: 'client-management',
      title: 'Client Management',
      icon: <Users className="w-5 h-5" />,
      content: [
        {
          title: 'Adding Clients',
          description: 'Create new client accounts individually or import in bulk.',
          steps: [
            'Click "Add Client" on the Clients page',
            'Enter client details: name, email, company, business type',
            'Optionally add phone, address, tax ID, and social media links',
            'Click "Create Client" to send an invitation email'
          ]
        },
        {
          title: 'Bulk Import',
          description: 'Import multiple clients at once using CSV:',
          steps: [
            'Click "Import" on the Clients page',
            'Download the CSV template for correct formatting',
            'Fill in client data (required: first_name, last_name, email, company_name)',
            'Upload your CSV file or paste the data directly',
            'Review and confirm the import'
          ]
        },
        {
          title: 'Client Status Management',
          description: 'Control client access with different status options:',
          steps: [
            'Active - Client has full portal access',
            'Suspended - Temporarily block access (can be reactivated)',
            'Revoked - Permanently block access (data preserved, can be reactivated)',
            'Archived - Remove from active list (can be restored)'
          ]
        },
        {
          title: 'Bulk Actions',
          description: 'Perform actions on multiple clients at once:',
          steps: [
            'Select clients using the checkboxes',
            'Use the bulk action bar that appears',
            'Choose: Suspend, Archive, or Delete',
            'Confirm the action in the dialog'
          ]
        },
        {
          title: 'Resend Invitations',
          description: 'Re-invite clients who haven\'t activated their portal:',
          steps: [
            'Open the client\'s profile page',
            'Look for the "Resend Invite" button (only visible for unactivated clients)',
            'Click to send a new invitation email with login credentials'
          ]
        },
        {
          title: 'Password Reset',
          description: 'Help clients who forgot their password:',
          steps: [
            'Open the client\'s profile page',
            'Click "Send Password Reset" button',
            'Client receives email with reset link (24-hour expiry)',
            'Client can also use "Forgot Password" on login page (1-hour expiry)'
          ]
        }
      ]
    },
    {
      id: 'document-management',
      title: 'Document Management',
      icon: <FileText className="w-5 h-5" />,
      content: [
        {
          title: 'Uploading Files',
          description: 'Share documents securely with clients:',
          steps: [
            'Navigate to a client\'s profile or the Files section',
            'Click "Upload File" or drag and drop',
            'Add tags to organize files (predefined or custom)',
            'Set visibility: Client-visible or Admin-only',
            'Files are automatically backed up to Google Drive'
          ]
        },
        {
          title: 'File Organization',
          description: 'Keep documents organized with tags and filters:',
          steps: [
            'Use predefined tags: Tax Returns, Financial Statements, Contracts, etc.',
            'Create custom tags for your specific needs',
            'Filter files by tag, upload date, or source (admin/client)',
            'Search files by name across all clients'
          ]
        },
        {
          title: 'File Preview',
          description: 'Preview documents without downloading:',
          steps: [
            'Click on any file to open the preview',
            'Supports: PDFs, images (JPG, PNG), and text files',
            'Download files using the download button',
            'Delete files you no longer need'
          ]
        },
        {
          title: 'Client Uploads',
          description: 'Clients can upload documents to their portal:',
          steps: [
            'Client uploads appear with "From Client" badge',
            'Review client uploads in the Files tab',
            'Receive notification when clients upload new files'
          ]
        }
      ]
    },
    {
      id: 'document-signing',
      title: 'Document Signing',
      icon: <PenTool className="w-5 h-5" />,
      content: [
        {
          title: 'Requesting Signatures',
          description: 'Get documents signed electronically:',
          steps: [
            'Upload a PDF document to the client\'s Files',
            'Go to the Signatures tab in client profile',
            'Click "Request Signature"',
            'Select the document and add a title/description',
            'Place signature fields on the PDF (Signature, Initial, Date, Text)',
            'Send the request - client receives email notification'
          ]
        },
        {
          title: 'Signature Field Types',
          description: 'Available annotation fields for documents:',
          steps: [
            'Signature - Full signature field',
            'Initial - Initials field',
            'Date - Auto-filled date field',
            'Text - Custom text input field'
          ]
        },
        {
          title: 'Tracking Signatures',
          description: 'Monitor signature request status:',
          steps: [
            'Pending - Waiting for client action',
            'Viewed - Client has opened the document',
            'Signed - Document successfully signed',
            'Declined - Client declined to sign',
            'View signed documents and audit trail'
          ]
        },
        {
          title: 'Client Signing Experience',
          description: 'What clients see when signing:',
          steps: [
            'Notification appears on client dashboard',
            'Click to open document with highlighted fields',
            'Use "Apply to All" for bulk signature filling',
            'Choose Draw or Type method for signatures',
            'Submit to complete signing'
          ]
        }
      ]
    },
    {
      id: 'messaging',
      title: 'Messaging System',
      icon: <MessageSquare className="w-5 h-5" />,
      content: [
        {
          title: 'Sending Messages',
          description: 'Communicate securely with clients:',
          steps: [
            'Go to Messages section or client\'s Messages tab',
            'Select the recipient client',
            'Type your message and optionally attach files',
            'Click Send - client receives email notification'
          ]
        },
        {
          title: 'Email Notifications',
          description: 'Automatic email alerts for messages:',
          steps: [
            'Admin to Client: Client receives email notification',
            'Client to Admin: All admins and sub-admins receive notification',
            'Customize notification templates in Settings > Templates'
          ]
        },
        {
          title: 'Message History',
          description: 'View complete conversation history:',
          steps: [
            'All messages are stored securely',
            'Search through message history',
            'Messages show read/unread status',
            'File attachments are linked to messages'
          ]
        }
      ]
    },
    {
      id: 'email-system',
      title: 'Email System',
      icon: <Mail className="w-5 h-5" />,
      content: [
        {
          title: 'Email Templates',
          description: 'Customize automated notification emails:',
          steps: [
            'Go to Settings > Templates',
            'Click on a template to edit',
            'Customize subject line and body content',
            'Use placeholders like [CLIENT_NAME], [PORTAL_URL]',
            'Preview your changes before saving'
          ]
        },
        {
          title: 'Available Templates',
          description: 'Pre-configured email templates:',
          steps: [
            'Client Portal Invitation - Welcome new clients',
            'Document Shared - Notify about new files',
            'New Message - Message notification',
            'Client Accepted Invitation - Admin notification',
            'Invoice Sent - Payment requests',
            'Payment Received - Confirmations',
            'Tax Deadline Reminder - Due date alerts'
          ]
        },
        {
          title: 'Composing Emails',
          description: 'Send custom emails to clients:',
          steps: [
            'Go to Settings > Compose',
            'Select recipients (multiple allowed)',
            'Write subject and body with rich text formatting',
            'Attach files from the file library',
            'Apply templates for quick composition'
          ]
        },
        {
          title: 'Email Settings',
          description: 'Configure email delivery options:',
          steps: [
            'Set sender name and from email',
            'Configure reply-to address',
            'Add default CC/BCC recipients',
            'Enable admin copy of all emails',
            'View email statistics and delivery status'
          ]
        },
        {
          title: 'Email Inbox',
          description: 'Track sent emails and their status:',
          steps: [
            'View all sent emails in Settings > Inbox',
            'Status tracking: Sent, Delivered, Opened, Clicked, Failed',
            'See CC/BCC recipients',
            'View email content and event timeline'
          ]
        }
      ]
    },
    {
      id: 'invoicing',
      title: 'Invoicing',
      icon: <DollarSign className="w-5 h-5" />,
      content: [
        {
          title: 'Creating Invoices',
          description: 'Generate professional invoices:',
          steps: [
            'Go to Invoicing section',
            'Click "Create Invoice"',
            'Select client and add line items',
            'Set due date and payment terms',
            'Send invoice - client receives email with payment link'
          ]
        },
        {
          title: 'Invoice Status',
          description: 'Track invoice lifecycle:',
          steps: [
            'Draft - Not yet sent',
            'Sent - Delivered to client',
            'Viewed - Client has opened',
            'Paid - Payment received',
            'Overdue - Past due date'
          ]
        },
        {
          title: 'Payment Integration',
          description: 'Accept payments via Stripe:',
          steps: [
            'Stripe integration for secure payments',
            'Clients pay directly from invoice email',
            'Automatic payment confirmation',
            'Payment history tracking'
          ]
        }
      ]
    },
    {
      id: 'tax-deadlines',
      title: 'Tax Deadlines',
      icon: <Calendar className="w-5 h-5" />,
      content: [
        {
          title: 'Managing Deadlines',
          description: 'Track important tax dates:',
          steps: [
            'Go to Tax Deadlines section',
            'Click "Add Deadline" to create new',
            'Set title, date, category, and description',
            'Mark as recurring for annual deadlines',
            'Archive completed deadlines'
          ]
        },
        {
          title: 'Deadline Categories',
          description: 'Organize deadlines by type:',
          steps: [
            'Individual - Personal tax deadlines',
            'Business - Corporate tax deadlines',
            'Quarterly - Estimated payments',
            'Annual - Year-end filings'
          ]
        },
        {
          title: 'Dashboard Widgets',
          description: 'Quick deadline overview:',
          steps: [
            'Total Active - All upcoming deadlines',
            'Overdue - Past-due items requiring attention',
            'This Week - Deadlines in next 7 days',
            'This Month - Deadlines in current month'
          ]
        }
      ]
    },
    {
      id: 'team-management',
      title: 'Team Management',
      icon: <Shield className="w-5 h-5" />,
      content: [
        {
          title: 'Adding Team Members',
          description: 'Invite staff to help manage the portal:',
          steps: [
            'Go to Team section',
            'Click "Add Team Member"',
            'Enter name, email, and select role',
            'Set specific permissions',
            'Team member receives invitation email'
          ]
        },
        {
          title: 'Roles and Permissions',
          description: 'Control access levels:',
          steps: [
            'Admin - Full access to all features',
            'Sub-Admin - Customizable permissions',
            'Available permissions: View clients, Edit clients, Delete clients, Archive clients, Manage team, View invoices, etc.'
          ]
        },
        {
          title: 'Managing Permissions',
          description: 'Fine-tune sub-admin access:',
          steps: [
            'Edit team member to modify permissions',
            'Only admins can delete clients (security feature)',
            'Sub-admins need explicit "archive_clients" permission',
            'Track team activity in Activity Logs'
          ]
        }
      ]
    },
    {
      id: 'activity-logs',
      title: 'Activity Logs',
      icon: <Clock className="w-5 h-5" />,
      content: [
        {
          title: 'Viewing Activity',
          description: 'Track all portal actions:',
          steps: [
            'Go to Settings > Activity Logs',
            'View both admin and client activities',
            'Filter by timeframe (Today, 7 days, 30 days, etc.)',
            'Filter by activity type'
          ]
        },
        {
          title: 'Activity Types',
          description: 'Actions that are logged:',
          steps: [
            'Client sign-ins and portal access',
            'File uploads and downloads',
            'Message sending and receiving',
            'Client archive/suspend/revoke actions',
            'Team member additions',
            'Password resets',
            'Document signatures'
          ]
        },
        {
          title: 'Dashboard Activity',
          description: 'Quick activity overview:',
          steps: [
            'Recent Activity widget on Dashboard',
            'Shows last 10 activities',
            'Click items to navigate to details',
            'Refresh to see latest updates'
          ]
        }
      ]
    },
    {
      id: 'leads',
      title: 'Lead Management',
      icon: <UserPlus className="w-5 h-5" />,
      content: [
        {
          title: 'Adding Leads',
          description: 'Track potential clients:',
          steps: [
            'Go to Leads section',
            'Click "Add Lead"',
            'Enter contact information and source',
            'Add notes and follow-up dates',
            'Track lead status through pipeline'
          ]
        },
        {
          title: 'Converting Leads',
          description: 'Turn leads into clients:',
          steps: [
            'Open lead profile',
            'Click "Convert to Client"',
            'All information transfers automatically',
            'Lead moves to Clients section'
          ]
        },
        {
          title: 'Lead Sources',
          description: 'Track where leads come from:',
          steps: [
            'Website inquiries',
            'Referrals',
            'Social media',
            'Cold calls',
            'Custom sources'
          ]
        }
      ]
    },
    {
      id: 'services',
      title: 'Service Packages',
      icon: <Building className="w-5 h-5" />,
      content: [
        {
          title: 'Creating Services',
          description: 'Define your service offerings:',
          steps: [
            'Go to Services section',
            'Click "Add Service"',
            'Enter name, description, and pricing',
            'Set service category',
            'Activate to make available'
          ]
        },
        {
          title: 'Using Services',
          description: 'Apply services to invoices:',
          steps: [
            'Services appear as line item options',
            'Quickly add pre-defined services to invoices',
            'Pricing auto-populates',
            'Customize per-invoice if needed'
          ]
        }
      ]
    },
    {
      id: 'security',
      title: 'Security & Privacy',
      icon: <Shield className="w-5 h-5" />,
      content: [
        {
          title: 'Authentication',
          description: 'Secure access controls:',
          steps: [
            'JWT-based authentication',
            'Password requirements enforced',
            'Session timeout for inactive users',
            'Google OAuth login option'
          ]
        },
        {
          title: 'Data Protection',
          description: 'How we protect your data:',
          steps: [
            'All data encrypted in transit (HTTPS)',
            'Secure file storage with backup to Google Drive',
            'Role-based access control',
            'Activity logging for audit trails'
          ]
        },
        {
          title: 'Client Privacy',
          description: 'Client data separation:',
          steps: [
            'Clients only see their own data',
            'Admin-only files hidden from clients',
            'Secure messaging between parties',
            'Controlled file visibility settings'
          ]
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: <HelpCircle className="w-5 h-5" />,
      content: [
        {
          title: 'Client Can\'t Login',
          description: 'Common login issues:',
          steps: [
            'Check if client status is Active (not Suspended/Revoked)',
            'Send password reset link from client profile',
            'Verify email address is correct',
            'Check spam folder for invitation email'
          ]
        },
        {
          title: 'Emails Not Sending',
          description: 'Email delivery issues:',
          steps: [
            'Check email configuration in Settings > Config',
            'Verify Mailgun API key is valid',
            'Check email statistics for failed sends',
            'Send test email to verify setup'
          ]
        },
        {
          title: 'File Upload Issues',
          description: 'File upload problems:',
          steps: [
            'Check file size (max 50MB)',
            'Verify file type is supported',
            'Try refreshing the page',
            'Check Google Drive connection'
          ]
        },
        {
          title: 'Missing Features',
          description: 'Can\'t see certain features:',
          steps: [
            'Check your role permissions',
            'Some features are admin-only',
            'Contact your administrator for access',
            'Refresh the page to reload permissions'
          ]
        }
      ]
    }
  ];

  const filteredSections = documentationSections.filter(section => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (section.title.toLowerCase().includes(query)) return true;
    return section.content.some(item => 
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query) ||
      (item.steps && item.steps.some(step => step.toLowerCase().includes(query)))
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <Book className="w-6 h-6 mr-2 text-teal-600" />
            Portal Documentation
          </h3>
          <p className="text-gray-600 mt-1">Complete guide to using the PPMT Client Portal</p>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="docs-search-input"
          />
        </div>
      </div>

      {/* Quick Links */}
      <div className="glass rounded-xl p-4">
        <h4 className="font-semibold text-gray-800 mb-3">Quick Links</h4>
        <div className="flex flex-wrap gap-2">
          {documentationSections.slice(0, 8).map(section => (
            <button
              key={section.id}
              onClick={() => {
                setExpandedSections([section.id]);
                document.getElementById(`section-${section.id}`)?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-teal-50 hover:text-teal-700 rounded-lg text-sm transition-colors"
            >
              {section.icon}
              <span>{section.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* What's New Section */}
      {showWhatsNew && (
        <div className="glass rounded-xl overflow-hidden border-2 border-teal-200" data-testid="whats-new-section">
          <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="w-5 h-5" />
              <h4 className="font-semibold">What's New</h4>
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {whatsNewItems.filter(i => i.isNew).length} new features
              </span>
            </div>
            <button 
              onClick={() => setShowWhatsNew(false)}
              className="text-white/80 hover:text-white text-sm"
            >
              Dismiss
            </button>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {whatsNewItems.map((item, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border transition-colors ${
                    item.isNew 
                      ? 'bg-teal-50 border-teal-200 hover:border-teal-300' 
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.isNew ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h5 className="font-medium text-gray-900 text-sm">{item.title}</h5>
                        {item.isNew && (
                          <span className="px-1.5 py-0.5 bg-teal-500 text-white text-xs rounded-full font-medium">
                            NEW
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                      <p className="text-xs text-gray-400 mt-1">{item.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Documentation Sections */}
      <div className="space-y-4">
        {filteredSections.map(section => (
          <div 
            key={section.id} 
            id={`section-${section.id}`}
            className="glass rounded-xl overflow-hidden"
            data-testid={`docs-section-${section.id}`}
          >
            {/* Section Header */}
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center text-white">
                  {section.icon}
                </div>
                <span className="font-semibold text-gray-900">{section.title}</span>
              </div>
              {expandedSections.includes(section.id) ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Section Content */}
            {expandedSections.includes(section.id) && (
              <div className="border-t border-gray-100 p-4 space-y-6">
                {section.content.map((item, index) => (
                  <div key={index} className="space-y-2">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-500" />
                      {item.title}
                    </h4>
                    <p className="text-gray-600 text-sm ml-6">{item.description}</p>
                    {item.steps && (
                      <ul className="ml-6 space-y-1">
                        {item.steps.map((step, stepIndex) => (
                          <li 
                            key={stepIndex}
                            className="text-sm text-gray-600 flex items-start gap-2"
                          >
                            <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500 flex-shrink-0 mt-0.5">
                              {stepIndex + 1}
                            </span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="glass rounded-xl p-6 text-center">
        <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h4 className="font-semibold text-gray-900 mb-2">Need More Help?</h4>
        <p className="text-gray-600 text-sm mb-4">
          Can't find what you're looking for? Contact support for assistance.
        </p>
        <a 
          href="mailto:support@ppmtgroup.com"
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-colors"
        >
          <Mail className="w-4 h-4" />
          Contact Support
        </a>
      </div>
    </div>
  );
};

export default Documentation;
